// Single rail entry. Carries the contract markup that
// `useTermCrossHighlight` and the embed-widget port both rely on:
//   <* class="maisight-glossary-row" data-term-id data-term>
//
// Definition precedence (PRD 00 §Pass 2 / PRD 07 §6.5):
//   1. canonical Definition from the matching CorpusEntity (Pass 2 output)
//   2. per-doc two-register Definition (definition_obj — v2 extractor)
//   3. per-doc bare definition string (v1 fallback)

import type { CorpusEntity, PublishedTerm } from "../data/types";
import { resolveTermFamily } from "../reading/termFamily";
import { scrollWithinRail } from "../reading/scrollWithinRail";
import { disclose, ORIGIN_ATTR } from "../reading/termOrigin";

export interface GlossaryRowProps {
  entry: PublishedTerm;
  active: boolean;
  canonical?: CorpusEntity | null;
  /** PRD 09 — sibling terms in the same doc, keyed by term_id, used to
   * render the "Built from: X · Y" disclosure for compound entries. */
  termsById?: ReadonlyMap<string, PublishedTerm>;
}

export function GlossaryRow({ entry, active, canonical, termsById }: GlossaryRowProps) {
  const definitionText = pickDefinitionText(entry, canonical);
  const includes = entry.includes ?? [];
  const builtFromConstituents =
    includes.length > 0 && termsById
      ? includes.map((id) => termsById.get(id)).filter((c): c is PublishedTerm => !!c)
      : [];
  const originDisclosure = disclose(entry.origin);
  return (
    <div
      data-term={entry.term}
      data-term-id={entry.term_id}
      data-term-family={resolveTermFamily(entry)}
      data-canonical-entity-id={canonical?.entity_id ?? undefined}
      data-includes={includes.length > 0 ? includes.join(" ") : undefined}
      {...{ [ORIGIN_ATTR]: entry.origin }}
      className={`maisight-glossary-row${active ? " is-active" : ""}`}
      tabIndex={active ? 0 : -1}
      aria-hidden={!active}
    >
      <dt className="maisight-glossary-term">
        {entry.term}
        {canonical ? (
          <span
            className="maisight-glossary-canonical-badge"
            title={`Canonical across ${canonical.member_refs.length} docs in this corpus`}
          >
            ↘
          </span>
        ) : null}
        <span
          className="maisight-origin-pill"
          {...{ [ORIGIN_ATTR]: entry.origin }}
          title={originDisclosure.longLabel}
          aria-label={originDisclosure.longLabel}
        >
          {originDisclosure.pillLabel}
        </span>
      </dt>
      <dd className="maisight-glossary-def">{definitionText}</dd>
      <p className="maisight-origin-footer" {...{ [ORIGIN_ATTR]: entry.origin }}>
        {originDisclosure.footerLabel}
      </p>
      {builtFromConstituents.length > 0 ? (
        <p className="maisight-glossary-built-from">
          <span className="maisight-glossary-built-from-label">Built from:</span>
          {builtFromConstituents.map((c, idx) => (
            <span key={c.term_id}>
              {idx > 0 ? (
                <span className="maisight-glossary-built-from-sep">·</span>
              ) : null}
              <button
                type="button"
                className="maisight-glossary-built-from-chip"
                data-term={c.term}
                data-term-id={c.term_id}
                onClick={(e) => {
                  const rail = e.currentTarget.closest<HTMLElement>(".maisight-rail");
                  const target = rail?.querySelector<HTMLElement>(
                    `.maisight-glossary-row[data-term-id="${CSS.escape(c.term_id)}"]`,
                  );
                  if (rail && target) scrollWithinRail(rail, target);
                }}
              >
                {c.term}
              </button>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

function pickDefinitionText(
  entry: PublishedTerm, canonical: CorpusEntity | null | undefined,
): string {
  if (canonical) return canonical.definition.definition_plain;
  if (entry.definition_obj) return entry.definition_obj.definition_plain;
  return entry.definition;
}
